import {
  AlertIcon,
  CurecallButton,
  CurecallList,
  CurecallListHeaders,
  CurecallPaginationBar,
  CurecallTooltip,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { getFullName } from 'src/utils/string-formatter.util'
import { TNavigationLocales } from 'src/types/locales'
import { EHRDelay, EHRDispatchStatus, THRCampaignDispatch } from 'src/types/hr'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { twMerge } from 'src/utils/tw-merge.util'
import { DispatchSmsLogsModal } from '../../_components/dispatch-sms-logs-modal.component'
import { formatPhone } from 'src/utils/phone.util'

type TDispatchHeaderName = 'Contact' | 'Phone' | 'Status' | 'Delay' | 'RespondedAt' | 'Actions'
type TTableRow = { [key in TDispatchHeaderName]: ReactNode }
type TSortState = { key: TDispatchHeaderName; descOrder: boolean }

const DISPATCH_STATUS_CLASSES: Record<EHRDispatchStatus, string> = {
  PENDING: 'c-bg-yellow-100 c-text-yellow-700',
  COMPLETED: 'c-bg-green-100 c-text-green-700',
  SUPERSEDED: 'c-bg-grey-100 c-text-grey-500',
  UNREACHABLE: 'c-bg-red-100 c-text-red-700',
}

const DELAY_CLASSES: Record<EHRDelay, string> = {
  MIN_30: 'c-bg-green-100 c-text-green-700',
  H1: 'c-bg-yellow-100 c-text-yellow-700',
  H2: 'c-bg-orange-200 c-text-orange-700',
  UNAVAILABLE: 'c-bg-red-100 c-text-red-700',
  PENDING_REVIEW: 'c-bg-orange-200 c-text-orange-700',
}

const DELAY_SORT_ORDER: Record<EHRDelay, number> = {
  MIN_30: 1,
  H1: 2,
  H2: 3,
  UNAVAILABLE: 4,
  PENDING_REVIEW: 5,
}

const ITEMS_PER_PAGE = 50

export const PlanBlancCampaignDispatchesTable = ({
  dispatches,
  campaignId,
}: Readonly<{
  dispatches: THRCampaignDispatch[]
  campaignId: number
}>) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const [selectedDispatch, setSelectedDispatch] = useState<THRCampaignDispatch | null>(null)
  const [sort, setSort] = useState<TSortState>({ key: 'Contact', descOrder: false })
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [dispatches])

  const handleSort = (key: TDispatchHeaderName) => {
    setSort((prev) => (prev.key === key ? { key, descOrder: !prev.descOrder } : { key, descOrder: false }))
    setCurrentPage(1)
  }

  const sortedDispatches = useMemo(() => {
    return [...dispatches].sort((a, b) => {
      let cmp = 0
      if (sort.key === 'Contact') {
        const aName = a.HRContact
          ? getFullName({
              firstname: a.HRContact.firstname ?? undefined,
              lastname: a.HRContact.lastname ?? undefined,
            }).trim()
          : ''
        const bName = b.HRContact
          ? getFullName({
              firstname: b.HRContact.firstname ?? undefined,
              lastname: b.HRContact.lastname ?? undefined,
            }).trim()
          : ''
        cmp = aName.localeCompare(bName, locale)
      } else if (sort.key === 'Status') {
        cmp = a.status.localeCompare(b.status)
      } else if (sort.key === 'Delay') {
        const aOrder = a.delay ? DELAY_SORT_ORDER[a.delay] : 99
        const bOrder = b.delay ? DELAY_SORT_ORDER[b.delay] : 99
        cmp = aOrder - bOrder
      } else if (sort.key === 'RespondedAt') {
        cmp = (a.respondedAt ?? '').localeCompare(b.respondedAt ?? '')
      }
      return sort.descOrder ? -cmp : cmp
    })
  }, [dispatches, sort])

  const dispatchStatusLabels: Record<EHRDispatchStatus, string> = {
    [EHRDispatchStatus.PENDING]: t('pages.hr.campaigns.detail.dispatch-status.PENDING'),
    [EHRDispatchStatus.COMPLETED]: t('pages.hr.campaigns.detail.dispatch-status.COMPLETED'),
    [EHRDispatchStatus.SUPERSEDED]: t('pages.hr.campaigns.detail.dispatch-status.SUPERSEDED'),
    [EHRDispatchStatus.UNREACHABLE]: t('pages.hr.campaigns.detail.dispatch-status.UNREACHABLE'),
  }

  const DELAY_LABELS: Record<EHRDelay, string> = {
    MIN_30: t('pages.hr.campaigns.detail.delay.MIN_30'),
    H1: t('pages.hr.campaigns.detail.delay.H1'),
    H2: t('pages.hr.campaigns.detail.delay.H2'),
    UNAVAILABLE: t('pages.hr.campaigns.detail.delay.UNAVAILABLE'),
    PENDING_REVIEW: t('pages.hr.campaigns.detail.delay.PENDING_REVIEW'),
  }

  const rows = sortedDispatches.map((dispatch) => {
    const contactName = dispatch.HRContact
      ? getFullName({
          firstname: dispatch.HRContact.firstname ?? undefined,
          lastname: dispatch.HRContact.lastname ?? undefined,
        }).trim() || '—'
      : '—'

    const statusBadge = (
      <span
        className={twMerge(
          'c-px-2 c-py-0.5 c-rounded-full c-text-xs c-font-medium',
          DISPATCH_STATUS_CLASSES[dispatch.status],
        )}
      >
        {dispatchStatusLabels[dispatch.status]}
      </span>
    )

    return {
      Contact: {
        observedData: contactName,
        component: <span>{contactName}</span>,
      },
      Phone: (
        <span className="c-flex c-items-center c-gap-1">
          {dispatch.HRContact ? formatPhone(dispatch.HRContact.phone) || dispatch.HRContact.phone : '—'}
          {dispatch.HRContact && !dispatch.HRContact.isPhoneValid && (
            <CurecallTooltip
              content={t('pages.hr.campaigns.detail.dispatches-table.invalid-phone-tooltip')}
              tooltipPosition="right"
              wrapperClassName="c-flex"
            >
              <AlertIcon className="c-h-4 c-w-4 c-text-red-500 c-shrink-0" />
            </CurecallTooltip>
          )}
        </span>
      ),
      Status: {
        observedData: dispatch.status,
        component:
          dispatch.status === EHRDispatchStatus.SUPERSEDED ? (
            <CurecallTooltip
              content={t('pages.hr.campaigns.detail.dispatch-status.superseded-tooltip')}
              tooltipPosition="top"
              wrapperClassName="c-h-max"
            >
              {statusBadge}
            </CurecallTooltip>
          ) : (
            statusBadge
          ),
      },
      Delay: {
        observedData: dispatch.delay ? DELAY_SORT_ORDER[dispatch.delay] : 99,
        component: dispatch.delay ? (
          <span
            className={twMerge(
              'c-px-2 c-py-0.5 c-rounded-full c-text-xs c-font-medium',
              DELAY_CLASSES[dispatch.delay],
            )}
          >
            {DELAY_LABELS[dispatch.delay]}
          </span>
        ) : (
          <span>—</span>
        ),
      },
      RespondedAt: {
        observedData: dispatch.respondedAt ?? '',
        component: (
          <span>{dispatch.respondedAt ? dayjs(dispatch.respondedAt).format('DD/MM/YYYY HH:mm') : '—'}</span>
        ),
      },
      Actions: (
        <CurecallButton
          appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
          size={ECurecallSize.SMALL}
          onClick={() => setSelectedDispatch(dispatch)}
        >
          {dispatch.delay === EHRDelay.PENDING_REVIEW
            ? t('pages.hr.campaigns.detail.dispatches-table.categorize')
            : t('pages.hr.campaigns.detail.dispatches-table.read-sms')}
        </CurecallButton>
      ),
    }
  })

  const headers: CurecallListHeaders<TTableRow> = [
    {
      label: t('pages.hr.campaigns.detail.dispatches-table.contact'),
      key: 'Contact',
      isSorted: true,
      onHeaderClick: () => handleSort('Contact'),
    },
    { label: t('pages.hr.campaigns.detail.dispatches-table.phone'), key: 'Phone' },
    {
      label: t('pages.hr.campaigns.detail.dispatches-table.status'),
      key: 'Status',
      isSorted: true,
      onHeaderClick: () => handleSort('Status'),
    },
    {
      label: t('pages.hr.campaigns.detail.dispatches-table.delay'),
      key: 'Delay',
      isSorted: true,
      onHeaderClick: () => handleSort('Delay'),
    },
    {
      label: t('pages.hr.campaigns.detail.dispatches-table.responded-at'),
      key: 'RespondedAt',
      isSorted: true,
      onHeaderClick: () => handleSort('RespondedAt'),
    },
    { label: '', key: 'Actions' },
  ]

  const paginatedRows = rows.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  return (
    <>
      <div className="c-flex-1 c-min-h-0 c-overflow-y-auto">
        <CurecallList rowsData={paginatedRows} headersData={headers} currentOrder={sort} />
      </div>
      {rows.length > ITEMS_PER_PAGE && (
        <CurecallPaginationBar
          totalItems={rows.length}
          itemsPerPage={ITEMS_PER_PAGE}
          currentPage={currentPage}
          numberOfVisiblePages={7}
          pageLabel={t('common.actions.page')}
          pagesLabel={t('common.actions.pages')}
          onClick={(page) => setCurrentPage(page)}
        />
      )}
      {selectedDispatch && (
        <DispatchSmsLogsModal
          dispatch={selectedDispatch}
          campaignId={campaignId}
          onClose={() => setSelectedDispatch(null)}
        />
      )}
    </>
  )
}
